/**
 * UrlGeneratorPage — “Scan URL → Auto-post” feature.
 *
 * Two-step flow:
 *   1. Scan a public URL → get a quick preview of the cleaned article
 *      (title, excerpt, word count, hero image, source).
 *   2. Pick platforms / tone / audience / output mode → kick off the
 *      generation pipeline. The same pipeline lives at
 *      `POST /api/url-generator/generate` and supports three modes:
 *        - drafts                  (text-only, saved with status="draft")
 *        - drafts_with_images      (text + 1 AI image per post, costs image quota)
 *        - auto_schedule           (text-only, scheduled at a fixed cadence)
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { FunnelCtaPicker, buildTrackedUrl } from '../components/FunnelCtaPicker';
import {
  Link2, Loader2, Globe, Sparkles, FileText, CalendarDays, ImageIcon,
  Instagram, Linkedin, Youtube, Twitter, ArrowRight, ExternalLink, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

const TONES = ['Professional', 'Educational', 'Motivational', 'Promotional', 'Casual', 'Friendly'];
const PLATFORMS = [
  { id: 'Instagram', label: 'Instagram', icon: <Instagram className="w-4 h-4" /> },
  { id: 'LinkedIn',  label: 'LinkedIn',  icon: <Linkedin className="w-4 h-4" /> },
  { id: 'Facebook',  label: 'Facebook',  icon: <span className="text-[10px] font-bold">FB</span> },
  { id: 'Bluesky',   label: 'Bluesky',   icon: <Twitter className="w-4 h-4" /> },
  { id: 'YouTube Shorts', label: 'YouTube Shorts', icon: <Youtube className="w-4 h-4" /> },
];

const MODES = [
  {
    id: 'drafts',
    title: 'Drafts only',
    desc: 'Save as text-only drafts in My Posts. Free — no image cost.',
    icon: FileText,
  },
  {
    id: 'drafts_with_images',
    title: 'Drafts + AI images',
    desc: 'Generate one AI image per post. Uses your image quota.',
    icon: ImageIcon,
  },
  {
    id: 'auto_schedule',
    title: 'Auto-schedule',
    desc: 'Drop posts into the calendar at a fixed cadence (text-only).',
    icon: CalendarDays,
  },
];

const isoNowPlusHours = (hours) => {
  const d = new Date(Date.now() + hours * 3600 * 1000);
  d.setMinutes(0, 0, 0);
  // Format as local-time string compatible with <input type="datetime-local">
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const UrlGeneratorPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();

  // Step 1 — scan
  const [url, setUrl] = useState('');
  const [scanning, setScanning] = useState(false);
  const [preview, setPreview] = useState(null); // {title, excerpt, word_count, hero_image, site_name, ...}

  // Step 2 — config
  const [platforms, setPlatforms] = useState(['Instagram', 'LinkedIn']);
  const [tone, setTone] = useState('Professional');
  const [audience, setAudience] = useState('');
  const [numPosts, setNumPosts] = useState(5);
  const [mode, setMode] = useState('drafts');
  const [imageModel, setImageModel] = useState('nano-banana');
  const [scheduleStart, setScheduleStart] = useState(isoNowPlusHours(2));
  const [scheduleInterval, setScheduleInterval] = useState(24);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [ctaFunnel, setCtaFunnel] = useState(null);

  const togglePlatform = (id) => {
    setPlatforms((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));
  };

  const handleScan = async () => {
    const trimmed = url.trim();
    if (!trimmed) {
      toast.error('Enter a URL to scan');
      return;
    }
    if (!/^https?:\/\//i.test(trimmed)) {
      toast.error('URL must start with http:// or https://');
      return;
    }
    setScanning(true);
    setPreview(null);
    setResult(null);
    try {
      const r = await api.post('/url-generator/scan', { url: trimmed });
      setPreview(r.data);
      toast.success(`Scanned: ${r.data.word_count} words extracted`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not scan that URL');
    } finally {
      setScanning(false);
    }
  };

  const handleGenerate = async () => {
    if (!preview) {
      toast.error('Scan a URL first');
      return;
    }
    if (platforms.length === 0) {
      toast.error('Select at least one platform');
      return;
    }
    if (!audience.trim()) {
      toast.error('Describe your audience');
      return;
    }
    setGenerating(true);
    setResult(null);
    try {
      const payload = {
        url: preview.url,
        platforms,
        tone,
        audience: audience.trim(),
        num_posts: Number(numPosts),
        mode,
      };
      if (ctaFunnel) {
        payload.cta_url = buildTrackedUrl(ctaFunnel, 'url-post');
        payload.cta_label = ctaFunnel.title;
      }
      if (mode === 'drafts_with_images') payload.image_model = imageModel;
      if (mode === 'auto_schedule') {
        // Convert local datetime-local to ISO
        const local = new Date(scheduleStart);
        payload.schedule_start = local.toISOString();
        payload.schedule_interval_hours = Number(scheduleInterval);
      }
      const r = await api.post('/url-generator/generate', payload);
      setResult(r.data);
      toast.success(`Generated ${r.data.count} posts`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleReset = () => {
    setUrl('');
    setPreview(null);
    setResult(null);
  };

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="url-generator-page">
      <div className="space-y-1">
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <Link2 className="w-7 h-7 text-indigo-600" />
          URL Generator
        </h1>
        <p className="text-zinc-500">
          Paste any blog post, article, or landing page URL — we'll extract the content and turn it
          into platform-ready social posts.
        </p>
      </div>

      {/* Step 1: URL input + scan */}
      <Card data-testid="url-scan-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-indigo-600" />
            1. Scan the source URL
          </CardTitle>
          <CardDescription>
            We'll fetch the page, strip the chrome, and show you what the AI will be working with.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              type="url"
              placeholder="https://yourblog.com/post-title"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              disabled={scanning}
              onKeyDown={(e) => { if (e.key === 'Enter') handleScan(); }}
              data-testid="url-input"
              className="flex-1"
            />
            <Button onClick={handleScan} disabled={scanning} data-testid="scan-url-btn">
              {scanning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Scan
            </Button>
            {preview && (
              <Button variant="ghost" onClick={handleReset} data-testid="reset-scan-btn">
                <RefreshCw className="w-4 h-4 mr-2" /> Reset
              </Button>
            )}
          </div>

          {preview && (
            <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4 space-y-3" data-testid="url-preview">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-zinc-900 truncate">{preview.title}</h3>
                  <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-zinc-500">
                    {preview.site_name && <Badge variant="secondary">{preview.site_name}</Badge>}
                    {preview.author && <span>by {preview.author}</span>}
                    <span>{preview.word_count.toLocaleString()} words</span>
                    <a
                      href={preview.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-indigo-600 hover:underline"
                    >
                      Open <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
                {preview.hero_image && (
                  <img
                    src={preview.hero_image}
                    alt=""
                    className="w-24 h-24 object-cover rounded-md border border-zinc-200 shrink-0"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                )}
              </div>
              <p className="text-sm text-zinc-700 leading-relaxed line-clamp-4">{preview.excerpt}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Step 2: Configuration */}
      {preview && (
        <Card data-testid="url-config-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-600" />
              2. Configure your posts
            </CardTitle>
            <CardDescription>How many, where, for whom, and what should happen after generation.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Target audience <span className="text-rose-500">*</span></Label>
                <Input
                  placeholder="e.g. small business owners on a tight marketing budget"
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  data-testid="audience-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Tone</Label>
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger data-testid="tone-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <FunnelCtaPicker
              value={ctaFunnel?.id || null}
              onChange={setCtaFunnel}
              refSource="url-post"
              label="Add a lead-capture CTA (optional)"
            />

            <div className="space-y-2">
              <Label>Platforms <span className="text-rose-500">*</span></Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {PLATFORMS.map((p) => {
                  const active = platforms.includes(p.id);
                  return (
                    <button
                      type="button"
                      key={p.id}
                      onClick={() => togglePlatform(p.id)}
                      data-testid={`platform-toggle-${p.id.toLowerCase().replace(/[^a-z]/g, '')}`}
                      className={`flex items-center gap-2 p-2 rounded-lg border text-sm text-left transition-all ${
                        active
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-900'
                          : 'border-zinc-200 hover:border-zinc-300 text-zinc-700'
                      }`}
                    >
                      <Checkbox checked={active} className="pointer-events-none" />
                      {p.icon}
                      <span>{p.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-1.5 max-w-xs">
              <Label>Number of posts</Label>
              <Input
                type="number"
                min={1}
                max={20}
                value={numPosts}
                onChange={(e) => setNumPosts(Math.max(1, Math.min(20, Number(e.target.value) || 1)))}
                data-testid="num-posts-input"
              />
              <p className="text-xs text-zinc-500">1–20. Distributed evenly across the platforms above.</p>
            </div>

            <div className="space-y-2">
              <Label>Output mode</Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {MODES.map((m) => {
                  const Icon = m.icon;
                  const active = mode === m.id;
                  return (
                    <button
                      type="button"
                      key={m.id}
                      onClick={() => setMode(m.id)}
                      data-testid={`mode-${m.id}`}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        active
                          ? 'border-violet-500 bg-violet-50'
                          : 'border-zinc-200 hover:border-zinc-300'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className={`w-4 h-4 ${active ? 'text-violet-600' : 'text-zinc-500'}`} />
                        <span className="font-medium text-sm">{m.title}</span>
                      </div>
                      <p className="text-xs text-zinc-500 leading-relaxed">{m.desc}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {mode === 'drafts_with_images' && (
              <div className="space-y-1.5 max-w-xs">
                <Label>Image model</Label>
                <Select value={imageModel} onValueChange={setImageModel}>
                  <SelectTrigger data-testid="image-model-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nano-banana">Gemini Nano Banana (default)</SelectItem>
                    <SelectItem value="gpt-image-1">OpenAI gpt-image-1</SelectItem>
                    <SelectItem value="dall-e-3">DALL·E 3</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {mode === 'auto_schedule' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Start at</Label>
                  <Input
                    type="datetime-local"
                    value={scheduleStart}
                    onChange={(e) => setScheduleStart(e.target.value)}
                    data-testid="schedule-start-input"
                  />
                  <p className="text-xs text-zinc-500">First post publishes at this time.</p>
                </div>
                <div className="space-y-1.5">
                  <Label>Hours between posts</Label>
                  <Input
                    type="number"
                    min={1}
                    max={168}
                    value={scheduleInterval}
                    onChange={(e) => setScheduleInterval(Math.max(1, Number(e.target.value) || 1))}
                    data-testid="schedule-interval-input"
                  />
                  <p className="text-xs text-zinc-500">24 = one per day. 168 = one per week.</p>
                </div>
              </div>
            )}

            <div className="pt-2 flex flex-wrap items-center gap-2">
              <Button
                onClick={handleGenerate}
                disabled={generating}
                size="lg"
                data-testid="generate-from-url-btn"
              >
                {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                Generate {numPosts} post{numPosts === 1 ? '' : 's'}
              </Button>
              {generating && mode === 'drafts_with_images' && (
                <p className="text-xs text-zinc-500">
                  Generating images sequentially — this can take 60–120 seconds.
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Result */}
      {result && (
        <Card data-testid="url-result-card" className="border-emerald-200 bg-emerald-50/50">
          <CardHeader>
            <CardTitle className="text-emerald-800 flex items-center gap-2">
              ✅ {result.count} posts generated
            </CardTitle>
            <CardDescription className="text-emerald-700">
              Mode: <code>{result.mode}</code>
              {result.images_generated > 0 && ` · ${result.images_generated} images attached`}
              {result.images_skipped_quota > 0 && ` · ${result.images_skipped_quota} images skipped (quota)`}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-auto">
              {result.posts.map((p) => (
                <div key={p.id} className="p-3 rounded-lg bg-white border border-zinc-200">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline">{p.platform}</Badge>
                    <Badge variant={p.status === 'scheduled' ? 'default' : 'secondary'}>{p.status}</Badge>
                  </div>
                  <p className="font-medium text-sm">{p.post_title}</p>
                  <p className="text-xs text-zinc-600 line-clamp-3 mt-1">{p.caption}</p>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              <Button onClick={() => navigate('/posts')} data-testid="goto-posts-btn">
                <FileText className="w-4 h-4 mr-2" /> Review in My Posts <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              {result.mode === 'auto_schedule' && (
                <Button variant="outline" onClick={() => navigate('/calendar')} data-testid="goto-calendar-btn">
                  <CalendarDays className="w-4 h-4 mr-2" /> Open Calendar
                </Button>
              )}
              <Button variant="ghost" onClick={handleReset} data-testid="scan-another-btn">
                Scan another URL
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default UrlGeneratorPage;
